import type { Icon } from "@nova/protobuf"
import type { Conforms } from "./shared"
import { buildIconKMZ, buildIconPng, type IconAssets } from "./src/kmz"

const loadImage = (source: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () =>
            reject(new Error("data-create-icon-kml: the browser could not decode the icon svg"))
        image.src = source
    })

const assets: IconAssets = {
    loadSvg: async (assetPath) => {
        const response = await fetch(assetPath)
        return await response.text()
    },
    encodePng: async (svgContent, sizeInPixels) => {
        const source = URL.createObjectURL(new Blob([svgContent], { type: "image/svg+xml" }))
        try {
            const image = await loadImage(source)

            const canvas = document.createElement("canvas")
            canvas.width = sizeInPixels
            canvas.height = sizeInPixels

            const context = canvas.getContext("2d")
            if (!context) {
                throw new Error("data-create-icon-kml: no 2d canvas context available")
            }
            context.drawImage(image, 0, 0, sizeInPixels, sizeInPixels)

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, "image/png")
            )
            if (!blob) {
                throw new Error("data-create-icon-kml: the canvas produced no png")
            }
            return new Uint8Array(await blob.arrayBuffer())
        } finally {
            URL.revokeObjectURL(source)
        }
    },
}

export const createIconKMZ = (...icons: Icon[]) => buildIconKMZ(assets, icons)

export const createIconPng = (icon: Pick<Icon, "url" | "color">) => buildIconPng(assets, icon)

type _Conforms = Conforms<typeof import("./browser")>
